import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, ChevronDown, ChevronRight, Bug } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showStack: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    showStack: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null, showStack: false };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error no React:', error, errorInfo);
    this.setState({ errorInfo });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private toggleStack = () => {
    this.setState((prev) => ({ showStack: !prev.showStack }));
  };

  public render() {
    if (this.state.hasError) {
      const { error, errorInfo, showStack } = this.state;

      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-red-100 dark:border-red-900/30 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 dark:from-red-700 dark:to-red-800 p-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-3">
                <AlertTriangle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">Algo deu errado</h2>
              <p className="text-red-100 text-sm mt-1">
                A interface encontrou um erro inesperado
              </p>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              {/* Error message */}
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Bug className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-red-800 dark:text-red-200">
                      {error?.name || 'Erro'}
                    </p>
                    <p className="text-sm text-red-700 dark:text-red-300 mt-1 break-words">
                      {error?.message || 'Nenhuma informação adicional disponível.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Stack trace (collapsible) */}
              {error?.stack && (
                <div className="border border-slate-200 dark:border-slate-600 rounded-lg overflow-hidden">
                  <button
                    onClick={this.toggleStack}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    <span>Detalhes técnicos</span>
                    {showStack ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                  {showStack && (
                    <pre className="m-0 p-4 text-xs text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 overflow-auto max-h-48 leading-relaxed">
                      {error.stack}
                      {errorInfo?.componentStack && (
                        <>
                          {'\n\n'}
                          <span className="font-semibold text-slate-500">Component Stack:</span>
                          {'\n'}
                          {errorInfo.componentStack}
                        </>
                      )}
                    </pre>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  onClick={this.handleReload}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white font-medium rounded-lg transition-colors shadow-sm"
                >
                  <RefreshCw className="w-4 h-4" />
                  Recarregar Página
                </button>
                <button
                  onClick={() => window.location.href = '/'}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-medium rounded-lg transition-colors"
                >
                  Ir para o Início
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-700">
              <p className="text-xs text-center text-slate-400 dark:text-slate-500">
                Se o problema persistir, entre em contato com o suporte informando o erro acima.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
